import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Dropdown } from 'primereact/dropdown'
import { classNames } from 'primereact/utils'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { apiClient } from '../../api/configs/axios-setup'
import {
  applyMetricFilterParams,
  PostProcImage,
  PostProcImageFilters,
  PostProcImagesResponse
} from '../../api/submission-API'
import MetricMultiSelect from './MetricMultiSelect'

type GifPlayerPanelProps = {
  systemRunId: string
  caseName: string
  runName?: string
  onClose?: () => void
  /** Opens the CreateGifModal seeded from this panel. */
  onGenerateGif?: () => void
  /** Opens the current frame in MAV. When omitted the button is hidden. */
  onOpenInMav?: (image: PostProcImage) => void
  /** Whether linked playback is active (shared index/playing across panels). */
  linked?: boolean
  onToggleLink?: () => void
  /** Controlled frame index — supplied when panels are linked. */
  index?: number
  onIndexChange?: (index: number) => void
  /** Controlled play state — supplied when panels are linked. */
  playing?: boolean
  onPlayingChange?: (playing: boolean) => void
}

const SPEED_OPTIONS = [
  { label: '0.5x', value: 0.5 },
  { label: '1x', value: 1 },
  { label: '2x', value: 2 },
  { label: '4x', value: 4 }
]
// Per-frame delay at 1x playback.
const BASE_DELAY_MS = 500
const MAX_IMAGES = 300

const fetchImages = async (
  systemRunId: string,
  filters: {
    imageType?: string
    section?: string
    location?: string
    metricFilters?: string[]
  } = {}
): Promise<PostProcImagesResponse> => {
  const params = new URLSearchParams({
    skip: '0',
    limit: String(MAX_IMAGES)
  })
  if (filters.imageType) params.set('imageType', filters.imageType)
  if (filters.section) params.set('section', filters.section)
  if (filters.location) params.set('location', filters.location)
  applyMetricFilterParams(params, filters.metricFilters)
  const res = await apiClient.get<PostProcImagesResponse>(
    `/WorkflowSubmissions/cases/${encodeURIComponent(systemRunId)}/images?${params.toString()}`
  )
  return res.data
}

const GifPlayerPanel: FC<GifPlayerPanelProps> = ({
  systemRunId,
  caseName,
  runName,
  onClose,
  onGenerateGif,
  onOpenInMav,
  linked,
  onToggleLink,
  index: controlledIndex,
  onIndexChange,
  playing: controlledPlaying,
  onPlayingChange
}) => {
  const [images, setImages] = useState<PostProcImage[]>([])
  const [availableFilters, setAvailableFilters] = useState<PostProcImageFilters | null>(null)
  const [loading, setLoading] = useState(true)

  const [imageTypeFilter, setImageTypeFilter] = useState<string>('all')
  const [sectionFilter, setSectionFilter] = useState<string>('all')
  const [locationFilter, setLocationFilter] = useState<string>('all')
  const [metricFilters, setMetricFilters] = useState<string[]>([])

  const [speed, setSpeed] = useState(1)
  const [loop, setLoop] = useState(true)

  const [internalIndex, setInternalIndex] = useState(0)
  const [internalPlaying, setInternalPlaying] = useState(false)

  // Support both controlled (linked) and uncontrolled playback.
  const currentIndex = controlledIndex ?? internalIndex
  const isPlaying = controlledPlaying ?? internalPlaying
  const setIndex = (updater: number | ((prev: number) => number)) => {
    const next = typeof updater === 'function' ? updater(currentIndex) : updater
    onIndexChange ? onIndexChange(next) : setInternalIndex(next)
  }
  const setPlaying = (value: boolean) => {
    onPlayingChange ? onPlayingChange(value) : setInternalPlaying(value)
  }

  useEffect(() => {
    let active = true
    setLoading(true)
    fetchImages(systemRunId, {
      imageType: imageTypeFilter === 'all' ? undefined : imageTypeFilter,
      section: sectionFilter === 'all' ? undefined : sectionFilter,
      location: locationFilter === 'all' ? undefined : locationFilter,
      metricFilters
    })
      .then(res => {
        if (!active) return
        setImages(res.images ?? [])
        // availableFilters covers every image for the case regardless of the filters applied.
        setAvailableFilters(res.availableFilters ?? null)
      })
      .catch(() => {
        if (active) toast.error(`Failed to load images for ${caseName}`)
      })
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [systemRunId, caseName, imageTypeFilter, sectionFilter, locationFilter, metricFilters])

  // Dropdown options come from the API so the lists stay complete regardless of the current filters.
  const imageTypeOptions = useMemo(
    () => availableFilters?.imageTypes ?? [],
    [availableFilters?.imageTypes]
  )
  const sectionOptions = useMemo(
    () => availableFilters?.sections ?? [],
    [availableFilters?.sections]
  )
  const locationOptions = useMemo(
    () => availableFilters?.locations ?? [],
    [availableFilters?.locations]
  )
  const imageTypeSelectOptions = useMemo(
    () => [
      { label: 'All Types', value: 'all' },
      ...imageTypeOptions.map(t => ({ label: t === 'Sect' ? 'Section' : t, value: t }))
    ],
    [imageTypeOptions]
  )
  const sectionSelectOptions = useMemo(
    () => [
      { label: 'All Sect.', value: 'all' },
      ...sectionOptions.map(s => ({ label: s, value: s }))
    ],
    [sectionOptions]
  )
  const locationSelectOptions = useMemo(
    () => [
      { label: 'All Loc.', value: 'all' },
      ...locationOptions.map(l => ({ label: l, value: l }))
    ],
    [locationOptions]
  )
  // Baseline metrics plus compare deltas encoded as "{metric}||{referenceRunId}".
  const metricOptions = useMemo(() => {
    const base = (availableFilters?.metricTypes ?? []).map(m => ({ label: m, value: m }))
    const compare = (availableFilters?.referenceMetrics ?? []).map(rm => ({
      label: `${rm.metricType} (${rm.referenceRunId})`,
      value: `${rm.metricType}||${rm.referenceRunId}`
    }))
    return [...base, ...compare]
  }, [availableFilters?.metricTypes, availableFilters?.referenceMetrics])

  const frames = images

  const total = frames.length
  const safeIndex = total > 0 ? Math.min(currentIndex, total - 1) : 0
  const currentImage = frames[safeIndex]

  // Reset to the first frame whenever the filtered set changes.
  const framesKey = `${imageTypeFilter}|${sectionFilter}|${locationFilter}|${metricFilters.join(
    ','
  )}|${total}`
  const prevFramesKey = useRef(framesKey)
  useEffect(() => {
    if (prevFramesKey.current !== framesKey) {
      prevFramesKey.current = framesKey
      setIndex(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [framesKey])

  // Playback loop.
  useEffect(() => {
    if (!isPlaying || total <= 1) return
    const id = setInterval(() => {
      setIndex(prev => {
        if (prev >= total - 1) {
          if (loop) return 0
          setPlaying(false)
          return prev
        }
        return prev + 1
      })
    }, BASE_DELAY_MS / speed)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, total, loop, speed])

  const step = (dir: -1 | 1) => {
    if (total === 0) return
    setIndex(prev => {
      const next = prev + dir
      if (next < 0) return total - 1
      if (next >= total) return 0
      return next
    })
  }

  const selectClass = 'text-xs w-36'
  const navArrowClass =
    'absolute top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-black/55 text-white shadow-lg transition-colors hover:bg-black/75 focus:outline-none'

  return (
    <div className="flex flex-col min-w-0 flex-1 bg-white dark:bg-neutral-90">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-800 dark:text-neutral-10 truncate">
            {caseName}
          </p>
          {runName && <p className="text-[10px] text-gray-400 truncate font-mono">{runName}</p>}
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          <Dropdown
            value={imageTypeFilter}
            options={imageTypeSelectOptions}
            optionLabel="label"
            optionValue="value"
            onChange={e => {
              setImageTypeFilter(e.value)
              setSectionFilter('all')
              setLocationFilter('all')
            }}
            className={selectClass}
            tooltip="Image type"
            tooltipOptions={{ position: 'top' }}
          />
          {imageTypeFilter !== 'Surface' && sectionOptions.length > 0 && (
            <Dropdown
              value={sectionFilter}
              options={sectionSelectOptions}
              optionLabel="label"
              optionValue="value"
              onChange={e => setSectionFilter(e.value)}
              className={selectClass}
              tooltip="Section"
              tooltipOptions={{ position: 'top' }}
            />
          )}
          {imageTypeFilter !== 'Sect' && locationOptions.length > 0 && (
            <Dropdown
              value={locationFilter}
              options={locationSelectOptions}
              optionLabel="label"
              optionValue="value"
              onChange={e => setLocationFilter(e.value)}
              className={selectClass}
              tooltip="Location"
              tooltipOptions={{ position: 'top' }}
            />
          )}
          <MetricMultiSelect
            value={metricFilters}
            options={metricOptions}
            onChange={setMetricFilters}
            className={selectClass}
            tooltip="Metrics"
          />
          {onToggleLink && (
            <Button
              icon={linked ? 'pi pi-lock' : 'pi pi-lock-open'}
              onClick={onToggleLink}
              size="small"
              severity={linked ? undefined : 'secondary'}
              tooltip={linked ? 'Playback linked' : 'Playback unlinked'}
              tooltipOptions={{ position: 'top' }}
              className="!w-7 !h-7 !p-0"
            />
          )}
          {onClose && (
            <Button
              icon="pi pi-times"
              onClick={onClose}
              size="small"
              text
              severity="secondary"
              className="!w-7 !h-7 !p-0"
            />
          )}
        </div>
      </div>

      {/* Image area */}
      <div className="relative flex-1 min-h-[280px] flex items-center justify-center bg-gray-100 dark:bg-neutral-80 overflow-hidden">
        {loading ? (
          <i className="pi pi-spin pi-spinner text-2xl text-gray-400" />
        ) : currentImage ? (
          <>
            <img
              src={currentImage.url}
              alt={currentImage.name}
              className="max-h-full max-w-full object-contain"
            />
            {total > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous frame"
                  className={classNames(navArrowClass, 'left-3')}
                  onClick={() => step(-1)}>
                  <i className="pi pi-chevron-left" />
                </button>
                <button
                  type="button"
                  aria-label="Next frame"
                  className={classNames(navArrowClass, 'right-3')}
                  onClick={() => step(1)}>
                  <i className="pi pi-chevron-right" />
                </button>
              </>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-400">No images match the current filters.</p>
        )}
      </div>

      {/* Control bar */}
      <div className="flex items-center gap-2.5 px-3.5 py-2 bg-gray-50 dark:bg-neutral-90 rounded-b">
        <button
          type="button"
          onClick={() => setPlaying(!isPlaying)}
          disabled={total <= 1}
          title={isPlaying ? 'Pause' : 'Play'}
          className="text-gray-600 dark:text-neutral-50 hover:text-gray-900 disabled:opacity-30">
          <i className={classNames('pi', isPlaying ? 'pi-pause' : 'pi-play')} />
        </button>
        <span className="font-mono text-xs text-gray-500 tabular-nums">
          {total === 0 ? '0 / 0' : `${safeIndex + 1} / ${total}`}
        </span>
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">Speed:</span>
          <select
            value={speed}
            onChange={e => setSpeed(Number(e.target.value))}
            className="border border-gray-200 dark:border-gray-600 rounded px-1.5 py-0.5 text-xs bg-white dark:bg-neutral-90">
            {SPEED_OPTIONS.map(o => (
              <option key={o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
          <Checkbox checked={loop} onChange={e => setLoop(e.checked ?? false)} />
          Loop
        </label>

        <div className="flex-1" />

        {onOpenInMav && (
          <Button
            label="Open in MAV"
            icon="pi pi-external-link"
            onClick={() => currentImage && onOpenInMav(currentImage)}
            disabled={!currentImage}
            size="small"
            className="!bg-[#048a9d] !border-[#048a9d] !text-white !text-xs !py-1 !px-2"
          />
        )}
        {onGenerateGif && (
          <Button
            label="Generate GIF"
            icon="pi pi-play"
            onClick={onGenerateGif}
            size="small"
            className="!bg-gray-700 !border-gray-700 !text-white !text-xs !py-1 !px-2"
          />
        )}
      </div>
    </div>
  )
}

export default GifPlayerPanel
